"use client";

import { Send, Loader2, Lightbulb, X, Zap } from "lucide-react";
import { useState, useEffect, useRef } from "react";

const ChatInput = ({
  value,
  onChange,
  onSubmit,
  isLoading,
  questions,
  refinedQueries = [],
  onQuestionClick,
  showQuestions,
  onToggleQuestions,
  hasMessages,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit();
  };

  const handleQuestionClick = (question) => {
    console.log("Question clicked:", question);
    // Call the onChange prop to update the parent's state
    onChange(question);

    // Focus the input if needed
    const inputElement = inputRef.current?.querySelector("input");
    if (inputElement) {
      inputElement.focus();
    }

    // Hide questions if shown
    if (showQuestions) {
      onToggleQuestions();
    }

    // Call the onQuestionClick prop which handles sending the message
    onQuestionClick(question);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (inputRef.current && !inputRef.current.contains(event.target)) {
        if (showQuestions && value.trim() === "") {
          onToggleQuestions();
        }
      }
    };

    // document.addEventListener("mousedown", handleClickOutside);
    // return () => {
    //   document.removeEventListener("mousedown", handleClickOutside);
    // };
  }, [showQuestions, value, onToggleQuestions]);

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 z-30 transition-all duration-500 ${
        hasMessages
          ? "bg-gradient-to-t from-white via-white/95 to-transparent backdrop-blur-xl"
          : "bg-transparent"
      }`}
    >
      <div
        className={`max-w-4xl mx-auto px-4 sm:px-6 transition-all duration-500 ${
          hasMessages ? "py-4" : "py-8"
        }`}
      >
        {/* Questions Pills */}
        {showQuestions && questions.length > 0 && (
          <div className="mb-4 animate-in slide-in-from-bottom-4 duration-300">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <Lightbulb className="w-3 h-3 text-white" />
                </div>
                <span className="text-sm font-medium text-gray-700">
                  Quick Start Questions
                </span>
              </div>
              <button
                onClick={onToggleQuestions}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {questions.map((question, index) => (
                <button
                  key={index}
                  onClick={() => handleQuestionClick(question)}
                  disabled={isLoading}
                  className="text-xs px-3 py-2 bg-white/80 hover:bg-white border border-gray-200 hover:border-blue-300 rounded-full transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 hover:text-blue-600 shadow-sm hover:shadow-md backdrop-blur-sm hover:scale-105"
                >
                  {question.length > 45
                    ? `${question.substring(0, 45)}...`
                    : question}
                </button>
              ))}
            </div>
          </div>
        )}
        {/* Refined Queries Suggestions 
        {refinedQueries.length > 0 && hasMessages && (
          <div className="mb-4 animate-in slide-in-from-bottom-4 duration-300">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 bg-gradient-to-r from-purple-500 to-pink-600 rounded-lg flex items-center justify-center">
                <Zap className="w-3 h-3 text-white" />
              </div>
              <span className="text-sm font-medium text-gray-700">
                Suggested Refined Queries
              </span>
            </div>
            <div className="flex flex-nowrap gap-2 overflow-x-auto scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent pb-2 -mx-1 px-1 scroll-smooth snap-x">
              {refinedQueries
                .slice(-5)
                .reverse()
                .map((query, index) => (
                  <button
                    key={index}
                    onClick={() => handleQuestionClick(query)}
                    disabled={isLoading}
                    className="text-xs px-3 py-2 bg-gradient-to-r from-purple-50 to-pink-50 hover:from-purple-100 hover:to-pink-100 border border-purple-200 hover:border-purple-300 rounded-full transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 hover:text-purple-600 shadow-sm hover:shadow-md backdrop-blur-sm hover:scale-105 flex-shrink-0 snap-start"
                  >
                    {query.length > 40 ? `${query.substring(0, 40)}...` : query}
                  </button>
                ))}
            </div>
          </div>
        )} */}
        
        {/* Input Form */}
        <form onSubmit={handleSubmit} className="relative" ref={inputRef}>
          <div
            className={`relative transition-all duration-500 ${
              isFocused || hasMessages ? "shadow-2xl" : "shadow-xl"
            }`}
          >
            <div className="relative">
              {/* Question Toggle Button - Show when questions are hidden and there are questions available */}
              {!showQuestions && questions.length > 0 && !value && (
                <button
                  type="button"
                  onClick={onToggleQuestions}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 p-2 hover:bg-blue-50 rounded-lg transition-colors z-10 group"
                  title="Show quick questions"
                >
                  <Lightbulb className="w-4 h-4 text-blue-500 group-hover:text-blue-600" />
                </button>
              )}

              <input
                type="text"
                value={value}
                onChange={(e) => {
                  onChange(e.target.value);
                  if (e.target.value.trim() && showQuestions) {
                    onToggleQuestions();
                  }
                }}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                placeholder="Your cybersecurity companion is listening. How can I assist?"
                disabled={isLoading}
                className={`w-full ${
                  !showQuestions && questions.length > 0 && !value
                    ? "pl-14"
                    : "pl-6"
                } pr-16 py-4 border-2 border-gray-200/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 disabled:opacity-50 disabled:cursor-not-allowed text-gray-800 placeholder-gray-500 bg-white/95 backdrop-blur-xl transition-all duration-300 ${
                  isFocused ? "bg-white shadow-2xl scale-[1.01]" : ""
                }`}
              />

              <button
                type="submit"
                disabled={isLoading || !value.trim()}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl hover:from-blue-600 hover:to-purple-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-lg font-medium hover:scale-105"
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChatInput;
